import {HTMLAttributes} from "react";
import {FormattedMessage, IntlShape} from "react-intl";
import "./session-expiration-watcher.scss";
import {Button, Modal} from "react-bootstrap";
import Notification from "../components/notification";
import {faUserSlash} from "@fortawesome/free-solid-svg-icons";

//<editor-fold desc="Props">

export enum WatcherState {
  OK,
  PROMPT,
  BLOCK,
}

export interface SessionExpirationWatcherContainerProps {
  expiresIn: number|undefined,
  setExpiresIn: (n: number|undefined) => void,
  showPrompt: WatcherState,
  setShowPrompt: (n: WatcherState) => void,
  promptDismissed: boolean,
  setPromptDismissed: (b:boolean) => void,
  logout: () => void,
  intl: IntlShape,
}
export interface SessionExpirationWatcherProps extends SessionExpirationWatcherContainerProps, HTMLAttributes<HTMLDivElement>{
  externalLogout?: boolean;
}
export default function SessionExpirationWatcher(
  props: SessionExpirationWatcherProps
) {
  const {
    externalLogout,
    expiresIn,
    setExpiresIn,
    showPrompt,
    setShowPrompt,
    promptDismissed,
    setPromptDismissed,
    logout,
    intl,
    ...other
  } = props;

  return (
    <>
      {showPrompt === WatcherState.PROMPT && expiresIn !== undefined && (
        <>
          <Notification
            id="sessionExpirationPrompt"
            type={"danger"}
            category={"user"}
            show={true}
            dismissible={!promptDismissed}
            icon={promptDismissed ? faUserSlash : undefined}
            asChild={promptDismissed}
            onClose={!promptDismissed ? () => setPromptDismissed(true) : undefined}
            {...other}
          >
            {promptDismissed && (
              <a
                className="alert-link"
                href="#dismiss"
                role={"button"}
                onClick={(e) => {
                  e.preventDefault();
                  setPromptDismissed(!promptDismissed);
                }}
              >
                {expiresIn <= 60 ? (
                  <strong className={"expires-in"}>
                    {intl.formatNumber(expiresIn, {
                      style: "unit",
                      unit: "second",
                      unitDisplay: "narrow",
                    })}
                  </strong>
                ) : (
                  <strong className={"expires-in"}>
                    {intl.formatNumber(Math.round(expiresIn / 60), {
                      style: "unit",
                      unit: "minute",
                      unitDisplay: "short",
                    })}
                  </strong>
                )}
              </a>
            )}
            {!promptDismissed && (<>
                <h4 className={"alert-heading"}>
                  <FormattedMessage
                      defaultMessage="Your session will expire {relativeTime}."
                      description="copy to be shown when the session is about to expire. 'relativeTime' = numeric relative time value with full unit type identifier (only seconds or minutes are used)"
                      values={{
                        relativeTime:
                            expiresIn && expiresIn <= 60
                                ? intl.formatRelativeTime(expiresIn, "second")
                                : intl.formatRelativeTime(
                                    Math.round(expiresIn / 60),
                                    "minute"
                                ),
                      }}
                  />
                </h4>
              <p>
                <FormattedMessage
                defaultMessage="If you wish to continue, finish up what you're working on and click refresh."
                description="Info copy to be shown when the session is about to expire. The intent is to give the user a heads up that they still have some time to complete what ever they are doing before their session expires."
                />
                </p>
                <Button
                type={"button"}
              variant={"danger"}
              onClick={() => window.location.reload()}
            >
              <FormattedMessage
                  defaultMessage="Refresh"
                  description={"refresh session button label"}
              />
            </Button>
            </>)}
          </Notification>
        </>
      )}
      {showPrompt === WatcherState.BLOCK && !externalLogout && (
        <Modal
          id="sessionExpirationBlock"
          data-test-session-expired
          show={true}
        >
          <Modal.Header>
            <Modal.Title>
              <FormattedMessage
                  defaultMessage="Your session has expired"
                  description="modal heading to be shown when the session has expired"
              />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
          <p>
            <FormattedMessage
              defaultMessage="Your session has expired. Please refresh or log out."
              description="message to be shown when the session has expired. Refresh is referring to the refresh session button, not refreshing the page."
            />
          </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={"primary"}
                    onClick={() => window.location.reload()}
            >
              <FormattedMessage
                  defaultMessage="Refresh"
                  description={"Refresh session button label"}
              />
            </Button>
            <Button variant={"danger"}
                    onClick={logout}
            >

              <FormattedMessage
                  defaultMessage="Logout"
                  description={"Logout button label"}
              />
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {showPrompt === WatcherState.BLOCK && externalLogout && (
        <Modal
          id="ExternalLogout"
          data-test-external-logout
          show={true}
        >
          <Modal.Header>
            <Modal.Title>
              <FormattedMessage
                  defaultMessage="You have been logged out"
                  description="modal heading to be shown when the user has been logged out in another tab or window"
              />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
          <p>
            <FormattedMessage
              defaultMessage="Your session has been terminated in another browser window or tab."
              description="info copy 1 to be shown when the user has been logged out in another tab or window."
            />
          </p>
          <p>
            <FormattedMessage
              defaultMessage="If you wish to continue you'll need to login again."
              description="info copy 2 to be shown when the user has been logged out in another tab or window."
            />
          </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant={"primary"}
                    onClick={() => window.location.reload()}
            >
              <FormattedMessage
                  defaultMessage="Continue"
                  description={"Continue button label"}
              />
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
